import React from 'react';
import { COLOR_MAP } from '../../constants/colorConstants';
import { getHoverOverlayMetrics, getStrokeWidth } from '../../utils/cableLogic';
import { normalizeColorToCanonical } from '../../utils/colorUtils';

const normalizeColorForVisualization = (color) => normalizeColorToCanonical(color);

const isEarthWire = (color) => {
  const normalized = normalizeColorForVisualization(color);
  return normalized === 'YE/GN';
};

const extractPathPoints = (d) => {
  const numericValues = String(d || '').match(/-?\d*\.?\d+/g) || [];
  const points = [];

  for (let i = 0; i < numericValues.length - 1; i += 2) {
    points.push({
      x: parseFloat(numericValues[i]),
      y: parseFloat(numericValues[i + 1])
    });
  }

  return points;
};

const getSplitGradientCoordinatesForSegment = (startPoint, endPoint, strokeWidth) => {
  const directionX = endPoint.x - startPoint.x;
  const directionY = endPoint.y - startPoint.y;

  const length = Math.hypot(directionX, directionY) || 1;
  const normalX = -directionY / length;
  const normalY = directionX / length;
  const halfStroke = Math.max(strokeWidth / 2, 1);
  const centerX = (startPoint.x + endPoint.x) / 2;
  const centerY = (startPoint.y + endPoint.y) / 2;

  return {
    x1: centerX - normalX * halfStroke,
    y1: centerY - normalY * halfStroke,
    x2: centerX + normalX * halfStroke,
    y2: centerY + normalY * halfStroke
  };
};

const getPathSegments = (d) => {
  const points = extractPathPoints(d);
  const segments = [];

  for (let i = 1; i < points.length; i += 1) {
    const start = points[i - 1];
    const end = points[i];

    if (Math.hypot(end.x - start.x, end.y - start.y) <= 0.001) {
      continue;
    }

    segments.push({
      start,
      end,
      d: `M ${start.x} ${start.y} L ${end.x} ${end.y}`
    });
  }

  return segments;
};

const CableSVGPath = ({
  cable,
  d,
  isHovered = false,
  opacity = 1,
  useAWG = false,
  className = '',
  ...otherProps
}) => {
  const isGialloVerde = isEarthWire(cable.colore);
  const strokeWidth = getStrokeWidth(cable.sezione, isHovered, useAWG);
  const hoverOverlayMetrics = getHoverOverlayMetrics(cable.sezione, useAWG);
  const gradientId = React.useId().replace(/:/g, '');
  const animatedStyle = isHovered ? {
    strokeDasharray: hoverOverlayMetrics.dashArray,
    '--cable-dash-travel': hoverOverlayMetrics.dashTravel,
    '--cable-dash-duration': hoverOverlayMetrics.animationDuration
  } : {};
  const mergedStyle = {
    ...(otherProps.style || {}),
    ...animatedStyle
  };

  if (isGialloVerde) {
    const segments = getPathSegments(d);

    if (segments.length > 1) {
      return (
        <>
          <defs>
            {segments.map((segment, index) => {
              const gradientCoords = getSplitGradientCoordinatesForSegment(segment.start, segment.end, strokeWidth);

              return (
                <linearGradient
                  key={`${gradientId}-${index}`}
                  id={`${gradientId}-${index}`}
                  gradientUnits="userSpaceOnUse"
                  x1={gradientCoords.x1}
                  y1={gradientCoords.y1}
                  x2={gradientCoords.x2}
                  y2={gradientCoords.y2}
                >
                  <stop offset="0%" stopColor={COLOR_MAP.YELLOW} />
                  <stop offset="50%" stopColor={COLOR_MAP.YELLOW} />
                  <stop offset="50%" stopColor={COLOR_MAP.GREEN} />
                  <stop offset="100%" stopColor={COLOR_MAP.GREEN} />
                </linearGradient>
              );
            })}
          </defs>
          {segments.map((segment, index) => (
            <path
              key={`${gradientId}-segment-${index}`}
              d={segment.d}
              stroke={`url(#${gradientId}-${index})`}
              strokeWidth={strokeWidth}
              opacity={opacity}
              fill="none"
              className={`cable-path ${isHovered ? 'animated' : ''} ${className}`}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={mergedStyle}
              {...otherProps}
            />
          ))}
        </>
      );
    }

    const fallbackSegment = segments[0] || {
      start: { x: 0, y: 0 },
      end: { x: 1, y: 0 }
    };
    const gradientCoords = getSplitGradientCoordinatesForSegment(
      fallbackSegment.start,
      fallbackSegment.end,
      strokeWidth
    );

    return (
      <>
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={gradientCoords.x1}
            y1={gradientCoords.y1}
            x2={gradientCoords.x2}
            y2={gradientCoords.y2}
          >
            <stop offset="0%" stopColor={COLOR_MAP.YELLOW} />
            <stop offset="50%" stopColor={COLOR_MAP.YELLOW} />
            <stop offset="50%" stopColor={COLOR_MAP.GREEN} />
            <stop offset="100%" stopColor={COLOR_MAP.GREEN} />
          </linearGradient>
        </defs>
        <path
          d={d}
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          opacity={opacity}
          fill="none"
          className={`cable-path ${isHovered ? 'animated' : ''} ${className}`}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={mergedStyle}
          {...otherProps}
        />
      </>
    );
  } else {
    // Path singolo per altri colori
    const solidColor = COLOR_MAP[normalizeColorForVisualization(cable.colore)] || '#808080';
    const isWhite = solidColor === '#FFFFFF';

    if (isWhite) {
      // Per cavi bianchi, aggiungi un bordo scuro per la visibilità
      return (
        <>
          {/* Bordo scuro per visibilità */}
          <path
            d={d}
            stroke="#666666"
            strokeWidth={strokeWidth + 1}
            fill="none"
            className={`cable-path-border ${isHovered ? 'animated' : ''} ${className}`}
            opacity={opacity}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={mergedStyle}
            {...otherProps}
          />
          {/* Path bianco principale */}
          <path
            d={d}
            stroke={solidColor}
            strokeWidth={strokeWidth}
            fill="none"
            className={`cable-path ${isHovered ? 'animated' : ''} ${className}`}
            opacity={opacity}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={mergedStyle}
            {...otherProps}
          />
        </>
      );
    } else {
      // Path normale per altri colori
      return (
        <path
          d={d}
          stroke={solidColor}
          strokeWidth={strokeWidth}
          fill="none"
          className={`cable-path ${isHovered ? 'animated' : ''} ${className}`}
          opacity={opacity}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={mergedStyle}
          {...otherProps}
        />
      );
    }
  }
};

export default CableSVGPath;


